/* ===================================================================
 * Movora motion layer: the behaviour half. Rules and reasoning live at the
 * top of assets/movora-motion.css; read that first.
 *
 * What this file does, in order:
 *   1. tags and sequences the reveal units (banner copy, rail headings, FAQ
 *      heading, and any [data-mv-group] a Movora section authored itself)
 *   2. splits display headings into words so each rises out of its own mask
 *   3. starts the hero immediately; everything else on IntersectionObserver
 *   4. fades lazy images in instead of letting them pop
 *   5. couples the kinetic tickers to scroll velocity
 *   6. animates <details> accordions open and shut
 *   7. pops the cart count and cascades the drawer lines on open
 *
 * FAILURE MODE IS "STATIC", NEVER "BLANK". layout/theme.liquid's inline head
 * script removes html.mv-motion after 3s unless window.__mvMotionReady is set,
 * and it is set only after the hero has been handed its reveal. Any exception
 * in here removes the class itself. With the class gone, every hidden state
 * in the CSS stops matching and the page is simply the page as designed.
 * =================================================================== */
(function () {
  'use strict';

  var html = document.documentElement;
  // Reduced motion, the theme editor, or the failsafe already fired.
  if (!html.classList.contains('mv-motion')) return;

  var EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var EASE_INOUT = 'cubic-bezier(0.65, 0, 0.35, 1)';
  var DUR_REVEAL = 1050;
  var DUR_WORD = 1150;
  var DUR_CHAR = 1300;
  var STEP_WORD = 70;
  var STEP_CHAR = 55;

  function bail(err) {
    html.classList.remove('mv-motion');
    if (window.console && err) console.warn('[movora-motion] disabled:', err);
  }

  function belowFold(el) {
    var r = el.getBoundingClientRect();
    return r.top > window.innerHeight * 0.92;
  }

  // ---------------------------------------------------------------- split --
  // Wraps every word (or every character) of the element's text nodes in
  // <span class="mv-w"><span class="mv-wi" style="--i:n">. Whitespace stays a
  // real text node between the spans, so wrapping and word spacing are the
  // browser's own and a screen reader still reads words, not letters.
  function wrapPiece(text, i) {
    var outer = document.createElement('span');
    outer.className = 'mv-w';
    var inner = document.createElement('span');
    inner.className = 'mv-wi';
    inner.style.setProperty('--i', i);
    inner.textContent = text;
    outer.appendChild(inner);
    return outer;
  }

  function split(el, mode) {
    if (el.classList.contains('mv-split-ready')) return el.__mvCount || 0;
    var original = el.textContent.replace(/\s+/g, ' ').trim();
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    var i = 0;
    nodes.forEach(function (node) {
      var text = node.nodeValue;
      if (!text || !text.trim()) return;
      var frag = document.createDocumentFragment();
      text.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        if (mode === 'chars') {
          // Letters of one word must not wrap apart: adjacent inline-blocks
          // are a line-break opportunity in Chrome.
          var word = document.createElement('span');
          word.style.whiteSpace = 'nowrap';
          Array.from(part).forEach(function (ch) {
            word.appendChild(wrapPiece(ch, i++));
          });
          frag.appendChild(word);
        } else {
          frag.appendChild(wrapPiece(part, i++));
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
    // Inline-block pieces can be announced one at a time (VoiceOver walks them
    // as separate runs, letters worst of all). Name the element with its whole
    // text and hide the pieces -- the same contract GSAP's SplitText uses. Only
    // where a name is allowed: headings and the decorative chars mode.
    if (mode === 'chars' || /^H[1-6]$/.test(el.tagName)) {
      el.setAttribute('aria-label', original);
      Array.prototype.forEach.call(el.children, function (c) {
        c.setAttribute('aria-hidden', 'true');
      });
    }
    el.__mvCount = i;
    el.classList.add('mv-split-ready');
    return i;
  }

  // ---------------------------------------------------------------- units --
  // A unit is one thing that reveals. Each records how long it needs so the
  // group can hand it back to its section's own styles (.is-done) on time;
  // a timer rather than animationend, which never fires for an element that
  // happens to be display:none at that moment.
  function makeUnit(el, kind, delay) {
    var total;
    if (kind === 'split' || kind === 'chars') {
      el.setAttribute('data-mv-split', kind === 'chars' ? 'chars' : 'words');
      var n = split(el, kind === 'chars' ? 'chars' : 'words');
      var step = kind === 'chars' ? STEP_CHAR : STEP_WORD;
      var dur = kind === 'chars' ? DUR_CHAR : DUR_WORD;
      total = delay * 1000 + Math.max(0, n - 1) * step + dur;
      el.style.setProperty('--mv-delay', delay + 's');
      return { el: el, total: total, words: n, step: step };
    }
    if (!el.hasAttribute('data-mv-reveal')) el.setAttribute('data-mv-reveal', kind === 'fade' ? 'fade' : '');
    el.style.setProperty('--mv-delay', delay + 's');
    total = delay * 1000 + DUR_REVEAL;
    return { el: el, total: total, words: 0, step: 0 };
  }

  function play(group) {
    if (group.played) return;
    group.played = true;
    if (group.host) group.host.classList.add('is-in');
    group.units.forEach(function (u) {
      u.el.classList.add('is-in');
      setTimeout(function () {
        u.el.classList.add('is-done');
      }, u.total + 160);
    });
  }

  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          var g = entry.target.__mvGroup;
          if (g) play(g);
        });
      },
      // Fire when the trigger's top is ~12% up from the bottom of the screen,
      // so the motion happens where the eye already is, not below the fold.
      { rootMargin: '0px 0px -12% 0px', threshold: 0 }
    );
  }

  function watch(group, trigger) {
    if (!group.units.length) return;
    if (!io) return play(group);
    trigger.__mvGroup = group;
    io.observe(trigger);
  }

  // Dawn's slide-in, when this file takes a block over. Removing the classes
  // before DOMContentLoaded keeps animations.js from ever observing it.
  function releaseFromDawn(el) {
    if (!el) return;
    el.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen');
  }

  // ------------------------------------------------------- banner copy --
  function initBanners() {
    var first = document.querySelector('#MainContent > .shopify-section:first-child');
    document.querySelectorAll('.mvm').forEach(function (banner) {
      var copy = banner.querySelector('.mvm__copy');
      if (!copy) return;
      var isHero = !!(first && first.contains(banner));
      // Only the hero has a CSS start state from first paint. Any other banner
      // already on screen at load is left exactly as it is: tagging it now
      // would flash its finished state, then hide it, then animate.
      if (!isHero && !belowFold(copy)) return;

      var t = isHero ? 0.35 : 0;
      var units = [];
      var eyebrow = copy.querySelector('.mvm__eyebrow');
      var title = copy.querySelector('.mvm__title');
      var sub = copy.querySelector('.mvm__sub');
      var btn = copy.querySelector('.mvm__btn');
      if (eyebrow) {
        units.push(makeUnit(eyebrow, 'rise', t));
        t += 0.1;
      }
      if (title) {
        var u = makeUnit(title, 'split', t);
        units.push(u);
        t += Math.max(1, u.words) * (STEP_WORD / 1000) + 0.18;
      }
      if (sub) {
        units.push(makeUnit(sub, 'rise', t));
        t += 0.1;
      }
      if (btn) units.push(makeUnit(btn, 'rise', t));

      var group = { units: units, host: banner };
      if (isHero) play(group);
      else watch(group, title || eyebrow || copy);
    });
  }

  // -------------------------------------------------------- rail headings --
  function initHeads() {
    var sel = '.mpg__head, .collapsible-content__header';
    document.querySelectorAll(sel).forEach(function (head) {
      if (!belowFold(head)) return; // Dawn's own reveal stays in charge of it
      var title = head.querySelector('.mpg__title, .collapsible-content__heading');
      if (!title) return;
      releaseFromDawn(head);
      var units = [];
      var u = makeUnit(title, 'split', 0);
      units.push(u);
      var rest = head.querySelectorAll('.mpg__link, .collapsible-content__caption, .caption-with-letter-spacing');
      var t = Math.max(1, u.words) * (STEP_WORD / 1000) + 0.15;
      rest.forEach(function (el) {
        if (el === title || title.contains(el)) return;
        units.push(makeUnit(el, 'fade', t));
        t += 0.08;
      });
      watch({ units: units, host: head }, title);
    });
  }

  // ---------------------------------------- groups authored by a section --
  // <div data-mv-group> containing [data-mv-reveal] / [data-mv-split] units.
  // A unit's own inline --mv-delay is respected; otherwise units are spaced
  // 0.08s apart in document order.
  function initAuthoredGroups(scope) {
    (scope || document).querySelectorAll('[data-mv-group]').forEach(function (host) {
      if (host.__mvInit) return;
      host.__mvInit = true;
      var units = [];
      var t = 0;
      host.querySelectorAll('[data-mv-reveal], [data-mv-split]').forEach(function (el) {
        var own = el.style.getPropertyValue('--mv-delay');
        var delay = own ? parseFloat(own) : t;
        var kind = el.hasAttribute('data-mv-split')
          ? el.getAttribute('data-mv-split') === 'chars'
            ? 'chars'
            : 'split'
          : el.getAttribute('data-mv-reveal') === 'fade'
          ? 'fade'
          : 'rise';
        units.push(makeUnit(el, kind, delay));
        t = delay + 0.08;
      });
      var group = { units: units, host: host };
      if (!units.length) {
        // A group with no units still gets .is-in, for CSS keyed on it
        // (the reassure icons draw off the group class alone).
        group.units = [];
        if (!io) host.classList.add('is-in');
        else {
          host.__mvGroup = { units: [], host: host, played: false };
          io.observe(host);
        }
        return;
      }
      watch(group, host.querySelector('[data-mv-trigger]') || host);
    });
  }

  // ----------------------------------------------------------- images --
  function initImages() {
    document.querySelectorAll('.mpg__img--base, .mvm__img, [data-mv-img]').forEach(function (img) {
      if (img.complete && img.naturalWidth) return;
      // Never the LCP candidate: an image at opacity 0 is not one.
      if (img.getAttribute('loading') === 'eager' || img.getAttribute('fetchpriority') === 'high') return;
      img.classList.add('mv-img-wait');
      var done = function () {
        img.classList.remove('mv-img-wait');
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  // ---------------------------------------------------------- tickers --
  // Each row is a CSS animation (translateX 0 -> -50% across two identical
  // groups). Scrolling feeds a velocity into the playbackRate of every visible
  // row, so the type surges with a flick and runs BACKWARDS while scrolling up,
  // then settles back to its own pace. The row also leans into the motion.
  var tickers = [];
  var vel = 0;
  var boost = 0;
  var lastY = window.scrollY;
  var lastT = performance.now();
  var looping = false;

  function fillRow(row) {
    var track = row.querySelector('.mvt__track');
    var groups = track ? track.querySelectorAll('.mvt__group') : [];
    if (groups.length < 2) return;
    // A seamless loop needs each group at least as wide as the row; on a very
    // wide screen the authored phrases can fall short. Clone until they do.
    var guard = 0;
    var source = Array.prototype.slice.call(groups[0].children);
    while (groups[0].scrollWidth < row.clientWidth + 40 && guard++ < 8) {
      groups.forEach(function (g) {
        source.forEach(function (n) {
          g.appendChild(n.cloneNode(true));
        });
      });
    }
  }

  function tick() {
    var target = Math.max(-4.5, Math.min(4.5, vel * 3.4));
    boost += (target - boost) * 0.1;
    vel *= 0.9;
    var settled = Math.abs(boost) < 0.004 && Math.abs(vel) < 0.002;
    if (settled) boost = 0;
    // Each row leans INTO its own direction of travel, so two rows running
    // opposite ways lean opposite ways. skewX(+) puts the top to the left.
    var lean = Math.max(-7, Math.min(7, boost * 1.8));
    tickers.forEach(function (t) {
      if (!t.visible || !t.anim || !t.react) return;
      t.anim.playbackRate = 1 + boost;
      var s = lean * t.dir;
      t.row.style.transform = s ? 'skewX(' + s.toFixed(2) + 'deg)' : '';
    });
    if (settled) {
      looping = false;
      return;
    }
    requestAnimationFrame(tick);
  }

  function kick() {
    if (looping) return;
    for (var i = 0; i < tickers.length; i++) {
      if (tickers[i].visible && tickers[i].react) {
        looping = true;
        requestAnimationFrame(tick);
        return;
      }
    }
  }

  function initTickers() {
    var roots = document.querySelectorAll('[data-mv-ticker]');
    if (!roots.length) return;
    var vis = io
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            tickers.forEach(function (t) {
              if (t.root === e.target) t.visible = e.isIntersecting;
            });
          });
        })
      : null;
    roots.forEach(function (root) {
      var react = root.hasAttribute('data-react');
      root.querySelectorAll('.mvt__row').forEach(function (row) {
        fillRow(row);
        var track = row.querySelector('.mvt__track');
        var anim = track && track.getAnimations ? track.getAnimations()[0] : null;
        tickers.push({
          root: root,
          row: row,
          anim: anim,
          react: react,
          dir: row.classList.contains('mvt__row--rev') ? -1 : 1,
          visible: !vis
        });
      });
      if (vis) vis.observe(root);
    });
    window.addEventListener(
      'scroll',
      function () {
        var now = performance.now();
        var y = window.scrollY;
        var dt = Math.max(8, now - lastT);
        vel = vel * 0.55 + ((y - lastY) / dt) * 0.45;
        lastY = y;
        lastT = now;
        kick();
      },
      { passive: true }
    );
  }

  // ------------------------------------------------------- accordions --
  // A <details> snaps open. This animates the content's height, margin and
  // opacity with the Web Animations API, then removes every inline trace so
  // the element is a plain <details> again at rest.
  function initAccordions() {
    document.querySelectorAll('.accordion details').forEach(function (d) {
      if (d.__mvAcc) return;
      var summary = d.querySelector(':scope > summary');
      var content = d.querySelector(':scope > .accordion__content');
      if (!summary || !content || !content.animate) return;
      d.__mvAcc = true;
      summary.addEventListener('click', function (e) {
        if (!html.classList.contains('mv-motion')) return;
        e.preventDefault();
        if (d.__mvBusy) return;
        d.__mvBusy = true;
        var cs = getComputedStyle(content);
        var mb = cs.marginBottom;
        content.style.overflow = 'hidden';
        var anim;
        if (!d.open) {
          d.open = true;
          var h = content.scrollHeight;
          anim = content.animate(
            [
              { height: '0px', marginBottom: '0px', opacity: 0, transform: 'translateY(-0.8rem)' },
              { height: h + 'px', marginBottom: mb, opacity: 1, transform: 'none' }
            ],
            { duration: 620, easing: EASE }
          );
          anim.onfinish = function () {
            content.style.overflow = '';
            d.__mvBusy = false;
          };
        } else {
          d.classList.add('mv-closing');
          anim = content.animate(
            [
              { height: content.offsetHeight + 'px', marginBottom: mb, opacity: 1 },
              { height: '0px', marginBottom: '0px', opacity: 0 }
            ],
            { duration: 460, easing: EASE_INOUT }
          );
          anim.onfinish = function () {
            d.open = false;
            d.classList.remove('mv-closing');
            content.style.overflow = '';
            d.__mvBusy = false;
          };
        }
        anim.oncancel = anim.onfinish;
      });
    });
  }

  // ------------------------------------------------- cart: count + drawer --
  function initCart() {
    var host = document.getElementById('cart-icon-bubble');
    if (host) {
      var read = function () {
        var n = host.querySelector('.cart-count-bubble [aria-hidden="true"]');
        return n ? parseInt(n.textContent, 10) || 0 : 0;
      };
      var last = read();
      new MutationObserver(function () {
        var now = read();
        if (now > last) {
          var b = host.querySelector('.cart-count-bubble');
          if (b) {
            b.classList.remove('mv-pop');
            void b.offsetWidth; // restart the animation on a second add
            b.classList.add('mv-pop');
          }
        }
        last = now;
      }).observe(host, { childList: true, subtree: true, characterData: true });
    }

    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      var wasOpen = drawer.classList.contains('active');
      var timer = null;
      new MutationObserver(function () {
        var open = drawer.classList.contains('active');
        if (open && !wasOpen) {
          drawer.classList.add('mv-opening');
          clearTimeout(timer);
          timer = setTimeout(function () {
            drawer.classList.remove('mv-opening');
          }, 1300);
        }
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // ------------------------------------------------------------- boot --
  try {
    initBanners(); // the hero first: the failsafe waits on this
    window.__mvMotionReady = true;
  } catch (err) {
    bail(err);
    return;
  }
  [initHeads, initAuthoredGroups, initImages, initTickers, initAccordions, initCart].forEach(function (fn) {
    try {
      fn();
    } catch (err) {
      // One broken feature must not strand content another one already hid.
      bail(err);
    }
  });
})();
